import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import CRMLayout from '../crm/CRMLayout';
import CRMDashboard from '../crm/pages/CRMDashboard';
import LeadsPage from '../crm/pages/LeadsPage';
import LeadDetailPage from '../crm/pages/LeadDetailPage';
import ContactsPage from '../crm/pages/ContactsPage';
import ContactDetailPage from '../crm/pages/ContactDetailPage';
import DealsPage from '../crm/pages/DealsPage';
import DealDetailPage from '../crm/pages/DealDetailPage';
import ActivitiesPage from '../crm/pages/ActivitiesPage';
import PropertiesPage from '../crm/pages/PropertiesPage';
import DuplicatesPage from '../crm/pages/DuplicatesPage';
import AuditPage from '../crm/pages/AuditPage';
import AutomationsPage from '../crm/pages/AutomationsPage';
import {
    clearCrmSessionToken,
    getCrmSessionActor,
    getCrmSessionToken,
    setCrmSessionActor,
} from '../crm/utils/crmEnv';
import { portalApiUrl } from './portalApi';

const normalizePortalRole = (value) => {
    const role = String(value || '').toLowerCase();
    if (role === 'admin' || role === 'manager' || role === 'agent') return role;
    return 'agent';
};

const hasRouteAccess = (role, allowedRoles) => allowedRoles.includes(normalizePortalRole(role));

const PortalCRMApp = () => {
    const { path } = useRouteMatch();
    const [portalRole, setPortalRole] = useState(() => normalizePortalRole(getCrmSessionActor()?.role));

    useEffect(() => {
        let isMounted = true;
        const checkSession = async () => {
            const token = getCrmSessionToken();
            if (!token) return;
            try {
                const response = await fetch(portalApiUrl('/api/crm-portal/portal/me'), {
                    headers: { 'X-CRM-SESSION': token }
                });
                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    if (response.status === 401) {
                        clearCrmSessionToken();
                        if (typeof window !== 'undefined') {
                            window.localStorage.setItem('crmSessionReplaced', new Date().toISOString());
                            if (!window.location.pathname.startsWith('/crm-portal/login')) {
                                window.location.href = '/crm-portal/login';
                            }
                        }
                        return;
                    }
                } else {
                    const data = await response.json().catch(() => null);
                    if (isMounted && data) {
                        const nextRole = normalizePortalRole(data.role);
                        setPortalRole(nextRole);
                        setCrmSessionActor({
                            seatId: data.seatId || null,
                            name: data.name || 'Teammate',
                            role: nextRole,
                            franchiseId: data.franchiseId || null,
                            expiresAt: data.expiresAt || null,
                        });
                    }
                }
            } catch {
                // Ignore transient failures
            }
        };

        const intervalId = window.setInterval(() => {
            if (isMounted) checkSession();
        }, 60000);

        checkSession();

        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
        };
    }, []);

    const renderRestrictedRoute = (routePath, Component, allowedRoles, exact = true) => (
        <Route
            exact={exact}
            path={routePath}
            render={(props) => (
                hasRouteAccess(portalRole, allowedRoles)
                    ? <Component {...props} />
                    : <Redirect to={`${path}/dashboard`} />
            )}
        />
    );

    return (
        <CRMLayout>
            <Switch>
                {renderRestrictedRoute(path, CRMDashboard, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/dashboard`, CRMDashboard, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/leads`, LeadsPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/leads/:id`, LeadDetailPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/contacts`, ContactsPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/contacts/:id`, ContactDetailPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/deals`, DealsPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/deals/:id`, DealDetailPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/properties`, PropertiesPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/properties/:id`, PropertiesPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/activities`, ActivitiesPage, ['agent', 'manager', 'admin'])}
                {renderRestrictedRoute(`${path}/audit`, AuditPage, ['manager', 'admin'])}
                {renderRestrictedRoute(`${path}/duplicates`, DuplicatesPage, ['admin'])}
                {renderRestrictedRoute(`${path}/automations`, AutomationsPage, ['admin'])}
                <Redirect to={`${path}/dashboard`} />
            </Switch>
        </CRMLayout>
    );
};

export default PortalCRMApp;
